import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Return a short-lived signed download URL for the caller's invoice on
 * an order. Buyers get the buyer invoice, sellers the seller invoice,
 * admins can request either party.
 *
 * Also lazy-generates the invoice PDFs if they don't yet exist (e.g. an
 * older paid order that predates the invoice system, or a webhook that
 * ran before this feature shipped).
 */
export const getOrderInvoiceUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({
      orderId: z.string().uuid(),
      party: z.enum(["buyer", "seller"]).optional(),
    }).parse(d),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;

    const { data: order, error } = await supabase
      .from("orders")
      .select("id,buyer_id,seller_id,status")
      .eq("id", data.orderId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!order) throw new Error("Order not found");

    const { data: isAdmin } = await supabase.rpc("has_role" as never, {
      _user_id: userId,
      _role: "admin",
    } as never);

    let party: "buyer" | "seller";
    if (data.party) {
      party = data.party;
      const allowed =
        isAdmin ||
        (party === "buyer" && order.buyer_id === userId) ||
        (party === "seller" && order.seller_id === userId);
      if (!allowed) throw new Error("Not authorized for this invoice");
    } else {
      if (order.buyer_id === userId) party = "buyer";
      else if (order.seller_id === userId) party = "seller";
      else if (isAdmin) party = "buyer";
      else throw new Error("Not authorized for this invoice");
    }

    const paidOrLater = ["paid", "ready_to_ship", "exported_to_click_drop", "shipped", "delivered", "released", "refunded"];
    if (!paidOrLater.includes(order.status)) {
      throw new Error("Invoice is available once payment is confirmed");
    }

    const { ensureInvoicesForOrder, getSignedInvoiceUrl } = await import("./invoice.server");
    const invoices = await ensureInvoicesForOrder(data.orderId);
    const inv = invoices[party];
    const url = await getSignedInvoiceUrl(inv.pdf_path);
    return { url, invoiceNumber: inv.invoice_number, party };
  });
